/**
 * Check if a value is a plain object (not null, array, Date, etc.).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

/**
 * Deep merge two objects with the following rules:
 * - Plain objects are merged recursively
 * - Arrays are replaced (not merged)
 * - `undefined` does not overwrite base
 * - `null` explicitly overwrites
 *
 * @param base - The base object
 * @param overrides - The overrides to apply
 * @returns A new merged object
 */
export function deepMerge<T>(base: T, overrides: Partial<T> | undefined): T {
  if (!overrides) {
    return { ...base };
  }

  const result = { ...base } as Record<string, unknown>;

  for (const key of Object.keys(overrides)) {
    const baseValue = result[key];
    const overrideValue = overrides[key as keyof typeof overrides];

    // undefined does not overwrite
    if (overrideValue === undefined) {
      continue;
    }

    // null explicitly overwrites
    if (overrideValue === null) {
      result[key] = null;
      continue;
    }

    // Arrays are replaced, not merged
    if (Array.isArray(overrideValue)) {
      result[key] = [...overrideValue];
      continue;
    }

    // Plain objects are merged recursively
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      result[key] = deepMerge(baseValue as Record<string, unknown>, overrideValue as Record<string, unknown>);
      continue;
    }

    // All other values (primitives, dates, etc.) are replaced
    result[key] = overrideValue;
  }

  return result as T;
}
